import { useCallback, useEffect, useState } from "react";
import api from "../api/axios";
import { APPROVALS_EVENT } from "../hooks/useApprovalsCount";
import "./ApprovalsPage.css";

type Status = "pending" | "approved" | "rejected";

interface Row {
  id: number;
  type: string;
  dealId: number | null;
  amount: number;
  reason: string | null;
  status: Status;
  requestedAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
  number: string | null;
  title: string | null;
  dealStatus: string | null;
  dealAmount: number | null;
  productsSum: number;
  clientName: string | null;
  requestedBy: string | null;
  decidedBy: string | null;
}

const TABS: { key: Status; label: string }[] = [
  { key: "pending", label: "На согласовании" },
  { key: "approved", label: "Одобрены" },
  { key: "rejected", label: "Отклонены" },
];

const TYPE_LABEL: Record<string, string> = { discount: "Скидка на чек" };

const money = (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n * 100) / 100) + " ₸";
const when = (s: string | null) =>
  s ? new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

export default function ApprovalsPage() {
  const [status, setStatus] = useState<Status>("pending");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    api
      .get<Row[]>("/approvals", { params: { status } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [status]);

  useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const done = () => {
    setRejecting(null);
    setNote("");
    window.dispatchEvent(new Event(APPROVALS_EVENT));
    load();
  };

  const act = async (id: number, fn: () => Promise<unknown>) => {
    setBusy(id);
    setError("");
    try {
      await fn();
      done();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setError(Array.isArray(m) ? m.join(", ") : m || "Не удалось выполнить действие");
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="apr-page">
      <h1 className="apr-title">Документы к согласованию</h1>
      <div className="apr-sub">Запросы сотрудников, которые решает администратор. Сейчас — скидки на чек.</div>

      <div className="apr-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={status === t.key ? "active" : ""} onClick={() => setStatus(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="apr-error">{error}</div>}

      {rows === null && <div className="apr-empty">Загрузка…</div>}
      {rows && rows.length === 0 && (
        <div className="apr-empty">{status === "pending" ? "Всё согласовано — запросов нет 👌" : "Здесь пока пусто"}</div>
      )}

      <div className="apr-list">
        {(rows ?? []).map((r) => {
          const pct = r.productsSum > 0 ? Math.round((r.amount / r.productsSum) * 1000) / 10 : 0;
          const closed = r.dealStatus && r.dealStatus !== "active";
          return (
            <div key={r.id} className="apr-card">
              <div className="apr-head">
                <span className="apr-type">🏷 {TYPE_LABEL[r.type] ?? r.type}</span>
                <span className="apr-time">{when(status === "pending" ? r.requestedAt : r.decidedAt || r.requestedAt)}</span>
              </div>
              <div className="apr-deal">
                {r.number ? <b>{r.number}</b> : "Сделка удалена"}
                {r.title ? <> · {r.title}</> : null}
              </div>
              {r.clientName ? <div className="apr-muted">Клиент: {r.clientName}</div> : null}
              <div className="apr-muted">Запросил: {r.requestedBy || "—"}</div>

              <div className="apr-sum">
                <div>
                  <span>Сумма позиций</span>
                  <b>{money(r.productsSum)}</b>
                </div>
                <div className="disc">
                  <span>Скидка{pct ? ` (${pct}%)` : ""}</span>
                  <b>− {money(r.amount)}</b>
                </div>
                <div>
                  <span>К оплате</span>
                  <b>{money(Math.max(0, r.productsSum - r.amount))}</b>
                </div>
              </div>

              {r.reason && <div className="apr-reason">«{r.reason}»</div>}

              {status === "pending" ? (
                <>
                  {closed && <div className="apr-error">Сделка уже завершена — скидку применить нельзя, запрос можно только отклонить.</div>}
                  {rejecting === r.id ? (
                    <div className="apr-reject">
                      <input
                        autoFocus
                        placeholder="Причина отказа (необязательно)"
                        value={note}
                        maxLength={300}
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <div className="apr-actions">
                        <button type="button" className="no" disabled={busy === r.id} onClick={() => act(r.id, () => api.post(`/approvals/${r.id}/reject`, { note }))}>
                          Отклонить
                        </button>
                        <button type="button" className="ghost" onClick={() => setRejecting(null)}>
                          Назад
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="apr-actions">
                      <button type="button" className="yes" disabled={busy === r.id || !!closed} onClick={() => act(r.id, () => api.post(`/approvals/${r.id}/approve`))}>
                        ✓ Одобрить
                      </button>
                      <button type="button" className="no" disabled={busy === r.id} onClick={() => { setRejecting(r.id); setNote(""); }}>
                        ✕ Отклонить
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={`apr-result ${r.status}`}>
                  {r.status === "approved" ? "Одобрено" : "Отклонено"}
                  {r.decidedBy ? ` · ${r.decidedBy}` : ""}
                  {r.decisionNote ? ` · ${r.decisionNote}` : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
